import { useEffect, useRef, type MouseEvent } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen, Pin, PinOff } from 'lucide-react'
import { LAYER } from '@/theme/layers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { useNavGroupDisclosure } from '@/hooks/useNavGroupDisclosure'
import { hasOverlayAbove, OVERLAY_LAYER_ATTR } from '@/utils/overlayLayers'
import { ConsoleHint } from '@/components/ui/console/ConsoleHint'
import { AlvearyMark } from './AlvearyMark'
import { IslandAccount } from './IslandAccount'
import { IslandNavGroup } from './IslandNavGroup'
import { ALL_NAV_ITEMS, type NavItem } from '@/config/navigation'

interface Props {
  open: boolean
  pinned: boolean
  isDesktop: boolean
  navigationId: string
  onToggle: () => void
  onExpand: () => void
  onClose: () => void
  onPin: () => void
  onSelect: () => void
}

/** Application navigation: route/capability composition over shared overlay mechanisms. */
export function IslandSidebar({
  open,
  pinned,
  isDesktop,
  navigationId,
  onToggle,
  onExpand,
  onClose,
  onPin,
  onSelect,
}: Props) {
  const { canAccessFeature } = useCurrentUser()
  const { pathname } = useLocation()
  const disclosure = useNavGroupDisclosure(open, pathname)
  const asideRef = useRef<HTMLElement>(null)
  const brandRef = useRef<HTMLButtonElement>(null)
  const modal = open && !isDesktop

  useFocusTrap(asideRef, modal)
  useBodyScrollLock(modal)

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      const target = event.target
      if (
        !pinned &&
        isDesktop &&
        target instanceof Element &&
        !asideRef.current?.contains(target) &&
        !target.closest('[data-navigation-toggle]') &&
        !hasOverlayAbove('float')
      )
        onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        hasOverlayAbove('float')
      )
        return
      event.preventDefault()
      brandRef.current?.focus()
      onClose()
    }
    // A panel arriving through history or asynchronous state owns the modal
    // layer. Release the mobile navigation trap before it can compete.
    const observer = pinned
      ? null
      : new MutationObserver(() => {
          if (hasOverlayAbove('float')) onClose()
        })
    observer?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [OVERLAY_LAYER_ATTR],
    })
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      observer?.disconnect()
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, pinned, isDesktop, onClose])

  const select = (event: MouseEvent<HTMLAnchorElement>) => {
    // Preserve browser link semantics, including opening a destination in a new tab.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return
    if (open && !pinned) brandRef.current?.focus()
    onSelect()
  }
  const items = ALL_NAV_ITEMS.flatMap<NavItem>((item) => {
    if (item.kind === 'leaf')
      return canAccessFeature(item.featureArea, item.minRank) ? [item] : []
    const children = item.children.filter((child) =>
      canAccessFeature(child.featureArea, child.minRank)
    )
    return children.length ? [{ ...item, children }] : []
  })

  return (
    <>
      {modal && (
        <div
          className="island-scrim"
          onClick={onClose}
          aria-hidden="true"
          data-testid="navigation-backdrop"
        />
      )}
      {/* The scale's floor: the rail floats over page content and is covered
          by every drawer, slide-over and modal that opens above it. */}
      <aside
        ref={asideRef}
        className={`alveary-navigation ${LAYER.float}`}
        data-open={open}
        data-pinned={pinned}
        data-testid="island-sidebar"
        role={modal ? 'dialog' : undefined}
        // eslint-disable-next-line no-restricted-syntax -- the mobile rail is modal for assistive technology without being a summoned surface
        aria-modal={modal ? true : undefined}
        aria-label="Application navigation"
        tabIndex={-1}
      >
        {/* Every hint is disabled while the rail is expanded: the label is
            already visible, and on mobile an expanded rail is the modal
            dialog, which must not host a second dismissable layer. */}
        <ConsoleHint label="Expand navigation" side="right" disabled={open}>
          <button
            ref={brandRef}
            type="button"
            className="island-brand"
            onClick={onToggle}
            data-navigation-toggle="true"
            aria-label={open ? 'Collapse navigation' : 'Expand navigation'}
            aria-expanded={open}
            aria-controls={navigationId}
            data-testid="navigation-toggle"
          >
            <span className="island-nav-icon">
              <AlvearyMark />
            </span>
            <span className="island-brand-word">
              <strong>Alveary</strong>
              <small>A space to think.</small>
            </span>
            {open && (
              <PanelLeftClose className="island-chevron" aria-hidden="true" />
            )}
          </button>
        </ConsoleHint>
        <nav
          id={navigationId}
          aria-label="Main navigation"
          className="island-navigation-scroll"
          data-testid="main-navigation"
        >
          <ul>
            {items.map((item) => {
              const Icon = item.icon
              return (
                <li
                  key={item.label}
                  className={
                    item.sectionStart ? 'island-section-start' : undefined
                  }
                >
                  {item.kind === 'group' ? (
                    <IslandNavGroup
                      item={item}
                      open={open}
                      expanded={disclosure.expanded.has(item.label)}
                      onToggle={() => disclosure.toggle(item.label)}
                      onReveal={() => disclosure.reveal(item.label)}
                      onExpand={onExpand}
                      onSelect={select}
                    />
                  ) : (
                    <ConsoleHint
                      label={item.label}
                      side="right"
                      disabled={open}
                    >
                      <NavLink
                        to={item.path}
                        end={item.path === '/'}
                        onClick={select}
                        className="island-nav-row"
                        aria-label={item.label}
                        data-testid={`nav-link-${item.path.slice(1) || 'home'}`}
                      >
                        <span className="island-nav-icon">
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className="island-nav-label">{item.label}</span>
                      </NavLink>
                    </ConsoleHint>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>
        <div className="island-footer">
          <div className="island-navigation-controls">
            <ConsoleHint
              label="Expand navigation"
              shortcut={'⌘ / Ctrl + \\'}
              side="right"
              disabled={open}
            >
              <button
                type="button"
                onClick={onToggle}
                className="island-nav-row"
                aria-label={open ? 'Minimise navigation' : 'Expand navigation'}
                aria-expanded={open}
                aria-controls={navigationId}
              >
                <span className="island-nav-icon">
                  {open ? (
                    <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
                  )}
                </span>
                <span className="island-nav-label">Minimise</span>
              </button>
            </ConsoleHint>
            {open && isDesktop && (
              <button
                type="button"
                className="island-pin"
                onClick={onPin}
                aria-label={pinned ? 'Unpin navigation' : 'Pin navigation open'}
                aria-pressed={pinned}
              >
                {pinned ? (
                  <PinOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Pin className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
          <IslandAccount navigationOpen={open} isDesktop={isDesktop} />
        </div>
      </aside>
    </>
  )
}
